import { apiClient, handleApiRequest } from './client'

export interface TeacherCodePattern {
  id: string
  school_id: string
  pattern_name: string
  pattern_syntax: string
  pattern_description: string | null
  learning_examples: Record<string, unknown>[] | null
  is_active: boolean
  version: number
  created_by_id: string | null
  created_at: string
}

export interface CodeMatchReport {
  total_codes: number
  matched: number
  unmatched: number
  average_confidence: number
  matches_by_method: Record<string, number>
  matches: Array<Record<string, unknown>>
  unmatched_codes: string[]
}

export const teacherCodeApi = {
  createPattern: (data: { examples: Array<{ name: string; code: string }>; custom_syntax?: string }) =>
    handleApiRequest<TeacherCodePattern>(apiClient.post('/api/v1/teacher-code/patterns', data)),

  listPatterns: () =>
    handleApiRequest<TeacherCodePattern[]>(apiClient.get('/api/v1/teacher-code/patterns')),

  validateSyntax: (data: { pattern_syntax: string }) =>
    handleApiRequest<{ valid: boolean; message?: string }>(apiClient.post('/api/v1/teacher-code/patterns/validate-syntax', data)),

  testPattern: (data: { syntax: string; test_name: string }) =>
    handleApiRequest<{ generated_code: string; confidence: number }>(apiClient.post('/api/v1/teacher-code/patterns/test', data)),

  getMatchReport: () =>
    handleApiRequest<CodeMatchReport>(apiClient.get('/api/v1/teacher-code/report')),

  listMatches: () =>
    handleApiRequest<Array<Record<string, unknown>>>(apiClient.get('/api/v1/teacher-code/matches')),

  validateMatch: (matchId: string, data: { is_valid: boolean; notes?: string }) =>
    handleApiRequest<Record<string, unknown>>(apiClient.post(`/api/v1/teacher-code/matches/${matchId}/validate`, data)),

  autoInferPattern: () =>
    handleApiRequest<Record<string, unknown>>(apiClient.post('/api/v1/teacher-code/patterns/auto-infer')),

  applyPatternToAll: () =>
    handleApiRequest<{ suggestions: Array<{ teacher_id: string; teacher_name: string; code: string; confidence: number }>; conflicts: Array<{ teacher_id: string; code: string; reason: string }> }>(apiClient.post('/api/v1/teacher-code/patterns/apply-to-all')),

  confirmAndApply: (assignments: Array<{ teacher_id: string; code: string }>) =>
    handleApiRequest<{ applied: number; errors: Array<Record<string, unknown>> }>(apiClient.post('/api/v1/teacher-code/patterns/confirm-and-apply', assignments)),

  getTeachersWithoutCodes: () =>
    handleApiRequest<Array<{ id: string; full_name: string; first_name: string; last_name: string }>>(apiClient.get('/api/v1/teacher-codes/without-codes')),

  previewCodes: (ruleType: string) =>
    handleApiRequest<{ suggestions: Array<{ teacher_id: string; teacher_name: string; code: string }>; conflicts: Array<{ teacher_id: string; code: string; reason: string }> }>(apiClient.post(`/api/v1/teacher-codes/preview?rule_type=${ruleType}`)),

  applyCodes: (assignments: Array<{ teacher_id: string; code: string }>) =>
    handleApiRequest<{ applied: number; errors: Array<Record<string, unknown>> }>(apiClient.post('/api/v1/teacher-codes/apply', assignments)),

  applyRule: (ruleType: string, confirmConflicts = false) =>
    handleApiRequest<{ applied: number; conflicts_skipped: number }>(apiClient.post(`/api/v1/teacher-codes/apply-rule?rule_type=${ruleType}&confirm_conflicts=${confirmConflicts}`)),

  getTeacherCodeStats: () =>
    handleApiRequest<{ total_teachers: number; with_code: number; without_code: number; active_pattern: { id: string; name: string; syntax: string } | null; auto_inferred_pattern: { pattern_syntax: string; confidence: number; matched_count: number; total_count: number; examples: Array<{ name: string; code: string }> } | null }>(apiClient.get('/api/v1/teacher-codes/stats')),

  suggestCodes: (data: { teachers: Array<{ first_name: string; last_name: string; teacher_id?: string }>; rule_type: string }) =>
    handleApiRequest<{ suggestions: Array<{ teacher_id: string | null; first_name: string; last_name: string; suggested_code: string; rule_type: string }> }>(apiClient.post('/api/v1/teacher-codes/suggest', data)),
}

export default teacherCodeApi
